/* rd-rsb2-addon.js
   Adds RSB2 add-on product together with the main product in ONE /cart/add.js call.
   Requires:
   - input (radio/checkbox) name="rd_addon_rsb2_choice"
   - when ON: value="1"
   - handle on the checked input: data-rd-addon-handle="oppgradert-rsb2-seterygg"
*/

(() => {
  "use strict";

  const ADDON_CHOICE_NAME = "rd_addon_rsb2_choice";
  const ADDON_VALUE_ON = "1";
  const DEFAULT_ADDON_HANDLE = "oppgradert-rsb2-seterygg";

  const IGNORE_KEYS = new Set([
    "id",
    "quantity",
    "form_type",
    "utf8",
    "section-id",
    "product-id",
    "sections",
    "sections_url",
  ]);

  const DEBUG = true;
  const log = (...a) => DEBUG && console.log("[RSB2]", ...a);

  let inFlight = false;
  const addonVariantCache = new Map(); // handle -> Promise<variantId>

  function stopAll(e) {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
  }

  function getQty(form) {
    const el =
      form.querySelector('input[name="quantity"]') ||
      form.querySelector('select[name="quantity"]');
    const n = el ? parseInt(el.value, 10) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  function buildProperties(form) {
    const fd = new FormData(form);
    const props = {};

    for (const [key, val] of fd.entries()) {
      if (val == null) continue;
      const s = String(val);
      if (!s.trim()) continue;

      if (IGNORE_KEYS.has(key)) continue;
      if (key.startsWith("options[")) continue;

      const m = key.match(/^properties\[(.+)\]$/);
      if (m) {
        props[m[1]] = s;
        continue;
      }

      // Treat other custom inputs as properties
      props[key] = s;
    }

    return Object.keys(props).length ? props : undefined;
  }

  function getAddonSelection() {
    // radio/checkbox supported
    const checked = document.querySelector(
      `input[name="${ADDON_CHOICE_NAME}"]:checked`
    );
    if (!checked) return { on: false, handle: null };

    // radio: must match value "1"
    if (checked.type === "radio" && String(checked.value) !== ADDON_VALUE_ON) {
      return { on: false, handle: null };
    }

    // checkbox: must be checked (already true here)
    if (checked.type === "checkbox" && !checked.checked) {
      return { on: false, handle: null };
    }

    const handle =
      checked.getAttribute("data-rd-addon-handle") ||
      checked.dataset.rdAddonHandle ||
      DEFAULT_ADDON_HANDLE;

    return { on: true, handle: handle || null };
  }

  function getMainVariantId(form) {
    const el = form.querySelector('input[name="id"]');
    const id = el ? Number(el.value) : 0;
    return Number.isFinite(id) && id > 0 ? id : 0;
  }

  async function fetchFirstVariantIdByHandle(handle) {
    const url = `/products/${encodeURIComponent(handle)}.js`;
    const r = await fetch(url, { headers: { Accept: "application/json" } });
    if (!r.ok) throw new Error(`Addon product .js fetch failed: ${r.status} ${url}`);
    const data = await r.json();

    const v =
      (data.variants || []).find((x) => x.available) ||
      (data.variants || [])[0];

    if (!v || !v.id) throw new Error("Addon product has no variants");
    return Number(v.id);
  }

  function getAddonVariantIdCached(handle) {
    if (!addonVariantCache.has(handle)) {
      addonVariantCache.set(handle, fetchFirstVariantIdByHandle(handle));
    }
    return addonVariantCache.get(handle);
  }

  async function addItems(items) {
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-RD-RSB2": "1", // marker so our fetch-patch ignores this call
      },
      body: JSON.stringify({ items }),
    });

    if (!r.ok) {
      let msg = `cart/add.js failed (${r.status})`;
      try {
        msg += `: ${await r.text()}`;
      } catch (_) {}
      throw new Error(msg);
    }

    return r.json();
  }

  function isCheckoutSubmitter(submitter) {
    if (!submitter) return false;
    const name = submitter.name || submitter.getAttribute("name") || "";
    if (name === "checkout") return true;
    const fa = submitter.getAttribute("formaction") || submitter.formAction || "";
    if (fa.includes("/checkout")) return true;
    if (submitter.classList && submitter.classList.contains("shopify-payment-button__button")) {
      return true;
    }
    return false;
  }

  async function addMainPlusAddonAndRedirect(form, submitter, addonHandle) {
    if (inFlight) return;
    inFlight = true;

    const wantCheckout = isCheckoutSubmitter(submitter);
    try {
      if (submitter) submitter.disabled = true;

      const mainId = getMainVariantId(form);
      if (!mainId) throw new Error("Missing main variant id (input[name=id])");

      const qty = getQty(form);
      const props = buildProperties(form);

      // main item
      const items = [
        { id: mainId, quantity: qty, ...(props ? { properties: props } : {}) },
      ];

      // addon item (same qty as main, so 2 rafts => 2 addons)
      try {
        const addonVariantId = await getAddonVariantIdCached(addonHandle);
        if (addonVariantId) {
          items.push({
            id: addonVariantId,
            quantity: qty,
            properties: { _addon: "RSB2", _parent: String(mainId) },
          });
        }
      } catch (e) {
        // If addon fetch fails, still add main product (do not block sale)
        console.warn("[RSB2] addon variant fetch failed, adding main only:", e);
      }

      log("adding items", items);
      await addItems(items);
      const target = wantCheckout ? "/checkout" : "/cart";
window.location.assign(target);
return;
    } catch (err) {
      console.error("[RSB2] add failed:", err);
      window.location.assign("/cart");
    } finally {
      if (submitter) submitter.disabled = false;
      inFlight = false;
    }
  }

  // 1) CAPTURE click on submit buttons (Venue often uses click->fetch)
  document.addEventListener(
    "click",
    (e) => {
      const btn = e.target.closest('button[type="submit"], input[type="submit"]');
      if (!btn) return;

      const form = btn.closest('form[action^="/cart/add"]');
      if (!form) return;

      const sel = getAddonSelection();
      if (!sel.on || !sel.handle) return; // RSB2 not selected -> normal theme flow

      stopAll(e);
      addMainPlusAddonAndRedirect(form, btn, sel.handle);
    },
    true
  );

  // 2) CAPTURE submit (Enter key / some themes still submit forms)
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target.closest('form[action^="/cart/add"]');
      if (!form) return;

      const sel = getAddonSelection();
      if (!sel.on || !sel.handle) return;

      stopAll(e);
      addMainPlusAddonAndRedirect(form, e.submitter || null, sel.handle);
    },
    true
  );

  // 3) Fallback: if theme calls fetch("/cart/add.js") directly, inject addon
  if (!window.__rdRsb2FetchPatched) {
    window.__rdRsb2FetchPatched = true;

    const origFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
      const url = typeof input === "string" ? input : input?.url;
      if (!url || !url.includes("/cart/add.js")) return origFetch(input, init);

      // Ignore our own calls
      try {
        const h = new Headers(init.headers || {});
        if (h.get("X-RD-RSB2") === "1") return origFetch(input, init);
      } catch (_) {}

      const sel = getAddonSelection();
      if (!sel.on || !sel.handle) return origFetch(input, init);

      // Only patch JSON string bodies (Venue usually does this)
      let payload = null;
      try {
        if (typeof init.body === "string") payload = JSON.parse(init.body);
      } catch (_) {}

      if (!payload) return origFetch(input, init);

      let itemsIn = Array.isArray(payload.items) ? payload.items : null;
      if (!itemsIn || itemsIn.length !== 1) return origFetch(input, init);

      const main = itemsIn[0];
      const mainId = Number(main.id);
      if (!mainId) return origFetch(input, init);

      const qty = Number(main.quantity || 1) || 1;

      // Move unknown keys into properties if needed
      let properties = main.properties;
      if (!properties) {
        const p = {};
        for (const [k, v] of Object.entries(main)) {
          if (k === "id" || k === "quantity" || k === "properties") continue;
          if (IGNORE_KEYS.has(k)) continue;
          if (k.startsWith("options[")) continue;
          if (v == null || v === "") continue;
          p[k] = v;
        }
        if (Object.keys(p).length) properties = p;
      }

      const addonVariantId = await getAddonVariantIdCached(sel.handle);
      if (!addonVariantId) return origFetch(input, init);

      // Avoid duplicates
      if (itemsIn.some((x) => Number(x.id) === Number(addonVariantId))) {
        return origFetch(input, init);
      }

      const newPayload = {
        items: [
          { id: mainId, quantity: qty, ...(properties ? { properties } : {}) },
          {
            id: Number(addonVariantId),
            quantity: qty,
            properties: { _addon: "RSB2", _parent: String(mainId) },
          },
        ],
      };

      const headers = new Headers(init.headers || {});
      headers.set("Content-Type", "application/json");
      headers.set("Accept", "application/json");

      log("injected into /cart/add.js", newPayload);

      return origFetch(url, {
        ...init,
        method: init.method || "POST",
        headers,
        body: JSON.stringify(newPayload),
      });
    };
  }

  // Optional: fill addon price text: <span data-rd-addon-price data-handle="oppgradert-rsb2-seterygg"></span>
  async function fillAddonPrices(root = document) {
    const els = root.querySelectorAll("[data-rd-addon-price][data-handle]");
    if (!els.length) return;

    const cache = new Map();

    for (const el of els) {
      const handle = el.getAttribute("data-handle");
      if (!handle) continue;

      try {
        if (!cache.has(handle)) {
          const r = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
            headers: { Accept: "application/json" },
          });
          if (!r.ok) throw new Error(`price fetch ${r.status}`);
          cache.set(handle, await r.json());
        }

        const p = cache.get(handle);
        const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
        if (!v) continue;

        const cents = v.price;
        const money =
          window.Shopify?.formatMoney
            ? window.Shopify.formatMoney(cents, window.Shopify.money_format)
            : (cents / 100).toFixed(2);

        el.textContent = ` (+${money})`;
      } catch (e) {
        console.warn("[RSB2] price fill failed", handle, e);
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => fillAddonPrices());
  } else {
    fillAddonPrices();
  }
  document.addEventListener("shopify:section:load", () => fillAddonPrices());

})();